(() => {
  'use strict';
  if (customElements.get('product-atf-global')) return;

  class ProductAtfGlobal extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const parse = selector => JSON.parse(this.querySelector(selector).textContent);
      this.style.display = 'block';
      try {
        this.root = this.querySelector('.hero');
        this.config = parse('[data-atf-config]');
        this.copy = parse('[data-grounding-copy]');
        this.variants = parse('[data-product-variants-json]');
        this.options = parse('[data-option-names-json]');
        this.groups = parse('[data-gallery-groups-json]');
        this.models = parse('[data-variant-model-info-json]');
        this.colorIndex = Number(this.root.dataset.colorIndex);
        this.sizeIndex = Number(this.root.dataset.sizeIndex);
        this.scentIndex = this.options.findIndex(name => name.toLowerCase() === 'scent');
        this.offerIndex = this.options.findIndex(name => name.toLowerCase() === 'offer');
        this.commerce = parse('[data-atf-commerce]');
        this.initializeOffer();
        this.initial = this.variants.find(variant => String(variant.id) === this.root.dataset.initialVariantId);
        this.money = cents => new Intl.NumberFormat(this.config.language, {style:'currency', currency:this.config.currency}).format(cents / 100);
        this.media = new Map([...this.querySelectorAll('[data-gallery-media]')].map(node => [node.dataset.galleryMedia, node]));
        this.renderedColor = this.initial.options[this.colorIndex];
        if(this.config.galleryGroups){
          const groups=JSON.parse(this.config.galleryGroups);
          if(!Array.isArray(groups)||!groups.length||!groups.every(group=>
            this.variants.some(variant=>variant.options[this.colorIndex]===group.optionValue)&&
            Array.isArray(group.mediaIds)&&group.mediaIds.length&&group.mediaIds.every(id=>this.media.has(String(id)))))
            throw new Error('Color media must belong to this product');
          this.groups=groups;this.renderedColor=null;
        }
        this.viewer = window.CloudNineGalleryViewer(this, '.equal-photo-card,.peek-slide,[data-lightbox-src]', card =>
          card.matches('.equal-photo-card,.peek-slide') ? this.models[this.selected()[0]?.id] : null);
        if (this.config.nativeBundle) this.initializeNativeSet();
        else this.initializeQuantitySelector(parse('[data-grounding-swatches]'));
        this.initializeFeaturePhotos();
        this.initializeReviewVideo();
        this.initializeTabs();
        this.initializeSizeGuide();
        this.track = this.querySelector('.peek-track');
        this.track.addEventListener('scroll', () => this.reflectProgress(), {passive:true});
        new ResizeObserver(() => this.reflectProgress()).observe(this.track);
        this.reflectProgress();
        this.querySelector('#modalConfirmBtn').addEventListener('click', () => this.addToCart());
        this.ready = true;
        this.validatePurchase();
      } catch (error) {
        this.querySelector('[data-atf-error]').textContent = this.querySelector('[data-atf-error]').dataset.message;
        console.error('Product ATF initialization failed', error);
      }
    }

    selected() {
      if (this.config.nativeBundle) return [this.nativeVariant(this.nativeKind)];
      const fields=[...this.querySelectorAll('#compactHoodieOptions .compact-hoodie-option')];
      return this.selector.getSelection().rows.map((row,index) => this.variants.find(variant =>
        variant.options[this.colorIndex] === row.color && variant.options[this.sizeIndex] === row.size &&
        (this.scentIndex < 0 || variant.options[this.scentIndex] === fields[index].querySelector('[data-atf-scent]').value)));
    }

    initializeScentControls() {
      if(this.scentIndex<0)return;
      this.querySelectorAll('#compactHoodieOptions .compact-hoodie-option').forEach((row,index) => {
        if(!row.querySelector('[data-atf-scent]')){
          row.append(this.querySelector('[data-atf-scent-control]').content.cloneNode(true));
          const select=row.querySelector('[data-atf-scent]');
          select.value=this.initial.options[this.scentIndex];
          select.setAttribute('aria-label',this.config.scentFor.replace('__number__',String(index+1)));
          select.addEventListener('change',()=>this.reflectSelection());
        }
        const select=row.querySelector('[data-atf-scent]'),color=row.querySelector('[data-option="color"]').value,size=row.querySelector('[data-option="size"]').value;
        [...select.options].forEach(option=>option.disabled=!this.variants.some(variant=>variant.available && variant.options[this.colorIndex]===color && variant.options[this.sizeIndex]===size && variant.options[this.scentIndex]===option.value));
        if(select.selectedOptions[0]?.disabled){const available=[...select.options].find(option=>!option.disabled);if(available)select.value=available.value;}
      });
    }

    initializeQuantitySelector(swatches) {
      const catalog = {
        colors: [...new Set(this.variants.map(variant => variant.options[this.colorIndex]))],
        sizes: [...new Set(this.variants.map(variant => variant.options[this.sizeIndex]))],
        swatches: Object.fromEntries(Object.entries(swatches).map(([color, css]) => {
          const {style} = document.createElement('span');
          style.cssText = css;
          return [color, {color:style.getPropertyValue('--swatch-color'), image:style.getPropertyValue('--swatch-image')}];
        })),
        variants: [...new Map(this.variants.map(variant => {
          const color=variant.options[this.colorIndex],size=variant.options[this.sizeIndex];
          const imageId=this.mediaIds(color)[0],image=this.media.get(imageId).content.querySelector('img');
          return [JSON.stringify([color,size]),{id:variant.id,color,size,
            availableForSale:this.variants.some(candidate=>candidate.available&&candidate.options[this.colorIndex]===color&&candidate.options[this.sizeIndex]===size),
            imageUrl:image.src,imageId}];
        })).values()]
      };
      this.selector = window.ProductATFOfferSelector(this.root, {
        catalog, defaultColor:this.initial.options[this.colorIndex], defaultSize:this.initial.options[this.sizeIndex],
        gifts:Object.fromEntries(this.config.tiers.map(tier=>[tier.quantity,[]])), copy:this.copy, defaultCount:Number(this.config.defaultQuantity),
        onPreview:({color,size,mediaId,trigger}) => {
          const ids = this.mediaIds(color);
          const scent=trigger.closest('.compact-hoodie-option').querySelector('[data-atf-scent]')?.value;
          const variant = this.variants.find(variant => variant.options[this.colorIndex] === color && variant.options[this.sizeIndex] === size && (this.scentIndex<0 || variant.options[this.scentIndex]===scent));
          this.viewer.open({photos:this.photos(ids), index:Math.max(0,ids.indexOf(mediaId)), trigger, modelNote:this.models[variant.id]});
        }
      });
      const gifts = {}, values = {};
      for (const tier of this.config.tiers) {
        const items = this.offer ? this.offer.tiers.find(rule=>rule.minimumQuantity===tier.quantity).gifts.map(key=>this.config.gifts.find(gift=>gift.key===key)) : this.config.gifts.slice(0,tier.giftCount);
        gifts[tier.quantity] = items.map(gift => ({...gift,value:this.money(gift.price)}));
        values[tier.quantity] = this.money(items.reduce((total,gift) => total+gift.price,0));
      }
      this.selector.setGifts(gifts,values);
      window.CloudNineGiftDetails(this.querySelector('#packageModal'), this.copy);
      this.root.addEventListener('grounding:offer-change', () => this.reflectSelection());
      this.reflectSelection();
      this.querySelector('#packageModal').removeAttribute('aria-busy');
    }

    reflectSelection() {
      this.initializeScentControls();
      const selected = this.selected();
      for (const tier of this.config.tiers) {
        const choices = Array.from({length:tier.quantity},(_,index) => selected[index] || selected[0]);
        const price = this.quote(tier,choices);
        const card = this.querySelector(`[data-bundle-count="${tier.quantity}"]`);
        card.querySelector('[data-offer-price]').textContent = this.money(this.offer?.pricing?price.total:Math.round(price.total/tier.quantity));
        card.querySelector('[data-offer-compare]').textContent = price.original>price.total ? this.money(this.offer?.pricing?price.original:Math.round(price.original/tier.quantity)) : '';
        card.querySelector('[data-offer-unit]').textContent = this.offer?.pricing?this.copy.packTotal:this.copy.unitLabel;
        card.querySelector('[data-offer-savings]').textContent = price.original>price.total ? this.message('percentOff',{percent:Math.round((price.original-price.total)/price.original*100)}) : '';
      }
      const tier = this.config.tiers.find(tier => tier.quantity===selected.length);
      const price = this.quote(tier,selected);
      this.renderPurchaseLabel(this.message(selected.length===1?'ctaOne':'ctaMany',{count:selected.length}),price);
      this.renderGallery(selected[0].options[this.colorIndex]);
    }

    renderPurchaseLabel(label,price) {
      const title = document.createElement('span');title.className='atf-cart-title';
      title.textContent=label;
      const button=this.querySelector('#modalConfirmBtn');
      this.cartIcon ||= button.querySelector('.cart-bag-icon');
      title.prepend(this.cartIcon);
      const details=document.createElement('span');details.className='atf-cart-details';
      const prices=document.createElement('span');prices.className='atf-cart-prices';
      if(price.original>price.total){const original=document.createElement('del');original.textContent=this.money(price.original);prices.append(original);}
      const current=document.createElement('strong');current.textContent=this.money(price.total);prices.append(current);details.append(prices);
      if(price.original>price.total){const savings=document.createElement('span');savings.className='atf-cart-savings-badge';savings.textContent=this.message('cartSavings',{amount:this.money(price.original-price.total)});details.append(savings);}
      button.querySelector('.cart-label').replaceChildren(title,details);
      this.validatePurchase();
    }

    initializeOffer() {
      if(this.config.quantityOffer){
        const engine=window.CloudNineQuantityOffers,offer=engine?.configuration(this.commerce.offer);
        this.commerceReady=false;
        if(!offer||offer.productId!==this.config.productId)return;
        this.offer=offer;this.giftCatalog=this.commerce.gifts;
        if(offer.pricing&&Object.keys(offer.gifts).length===1){this.copy.giftSummary=this.copy.singleGiftSummary;this.copy.includedGifts=this.copy.singleGiftIncluded;}
        this.config.tiers=offer.tiers.map(tier=>({quantity:tier.minimumQuantity,percentage:tier.percentage,giftCount:tier.gifts.length}));
        this.config.gifts=Object.entries(offer.gifts).map(([key,id])=>{
          const gift=this.giftCatalog.find(gift=>gift.id===id);
          if(!gift)throw new Error('Missing quantity-offer gift reference');
          return {...gift,key};
        });
        this.commerceReady=!this.config.review&&engine.matches(offer);
        return;
      }
      if(this.config.review){
        if(!['unpublished','development'].includes(window.Shopify?.theme?.role))throw new Error('Review is restricted to unpublished themes');
        this.commerceReady=false;return;
      }
      if(this.config.nativeBundle){
        const pants=this.config.nativePants,bundle=this.config.nativeBundle;
        if(!Array.isArray(pants?.options)||!['Color','Size'].every(name=>pants.options.includes(name))||
          !Array.isArray(pants.galleries)||!bundle.variants.every(variant=>pants.galleries.some(group=>
            group.color===variant.options[bundle.options.indexOf('Color')]&&Array.isArray(group.photos)&&group.photos.length>0&&
            group.photos.every(photo=>typeof photo.src==='string'&&photo.src)))||
          !Array.isArray(pants.variants)||!bundle.variants.every(variant=>pants.variants.some(item=>
            item.options[pants.options.indexOf('Color')]===variant.options[bundle.options.indexOf('Color')]&&
            item.options[pants.options.indexOf('Size')]===variant.options[bundle.options.indexOf('Size')]&&typeof item.image==='string'&&item.image)))
          throw new Error('Travel Set requires a matching pants catalog');
        this.commerceReady=true;return;
      }
      const groundingCatalog=this.querySelector('[data-grounding-offer-catalog]');
      const data=groundingCatalog?JSON.parse(groundingCatalog.textContent):this.commerce;
      const offer=groundingCatalog?window.GroundingOffer?.config:data.offer;
      const positive=value=>Number.isSafeInteger(value)&&value>0;
      const valid=(!!groundingCatalog||window.CloudNineProductOffers?.matches(offer))&&
        offer?.version===1&&offer.enabled===true&&typeof offer.offerId==='string'&&
        Array.isArray(offer.eligibleProductIds)&&offer.eligibleProductIds.includes(this.config.productId)&&
        offer.eligibleProductIds.every(positive)&&offer.gifts&&Array.isArray(offer.tiers)&&offer.tiers.length===3&&
        offer.tiers.every((tier,index)=>tier.minimumQuantity===[1,2,4][index]&&Number.isInteger(tier.percentage)&&tier.percentage>=0&&tier.percentage<=100&&
          Array.isArray(tier.gifts)&&new Set(tier.gifts).size===tier.gifts.length&&tier.gifts.every(key=>positive(offer.gifts[key])));
      this.commerceReady=!!valid;
      if(!valid)return;
      this.offer=offer;
      this.giftCatalog=data.gifts;
      this.config.tiers=offer.tiers.map(tier=>({quantity:tier.minimumQuantity,percentage:tier.percentage,giftCount:tier.gifts.length}));
      this.config.gifts=Object.entries(offer.gifts).map(([key,id])=>{
        const gift=data.gifts.find(gift=>gift.id===id);
        if(!gift)throw new Error('Missing native gift catalog');
        return {...gift,key};
      });
    }

    cartItems(instance) {
      const selected=this.selected();
      if(!this.commerceReady)throw new Error('pending');
      if(!selected.length||selected.some(variant=>!variant?.available))throw new Error('unavailable');
      if(this.config.nativeBundle)return [{id:selected[0].id,quantity:1,
        ...(this.config.preorderMessage?{properties:{'Pre-order':this.config.preorderMessage}}:{})}];
      if(this.config.quantityOffer)return window.CloudNineQuantityOffers.items(this.offer,selected,this.giftCatalog,instance);
      return window.GroundingOffer.items(this.offer,selected,this.giftCatalog,instance);
    }

    validatePurchase() {
      const button=this.querySelector('#modalConfirmBtn'),status=this.querySelector('.commerce-status');
      if(this.adding){button.disabled=true;return;}
      try{
        this.cartItems('validation');
        button.disabled=false;
        status.textContent='';
      }catch(error){
        button.disabled=true;
        status.textContent=this.commerce[error.message]||this.commerce.pending;
      }
    }

    async addToCart() {
      const button=this.querySelector('#modalConfirmBtn');
      if(this.adding||button.disabled)return;
      const status=this.querySelector('.commerce-status'),host=this.querySelector('#packageModal');
      let added=false;
      this.adding=true;
      button.disabled=true;
      button.setAttribute('data-adding','true');
      host.inert=true;
      try{
        const drawer=window.kachingCartApi;
        if(typeof drawer?.refreshCart!=='function'||typeof drawer?.openCart!=='function')throw new Error('cart_loading');
        const items=this.cartItems(crypto.randomUUID());
        button.querySelector('.cart-label').textContent=this.commerce.adding;
        const response=await fetch(`${window.Shopify.routes.root}cart/add.js`,{
          method:'POST',headers:{'Content-Type':'application/json','X-Kaching-Cart-Ignore':'1'},
          body:JSON.stringify({items:items.reverse()})
        });
        const result=await response.json();
        if(!response.ok)throw new Error(typeof result.description==='string'?result.description:this.commerce.add_failed);
        added=true;
        await drawer.refreshCart();
        await drawer.openCart();
      }catch(error){
        if(added){window.location.assign(`${window.Shopify.routes.root}cart`);return;}
        this.purchaseError=this.commerce[error.message]||error.message||this.commerce.add_failed;
      }finally{
        host.inert=false;
        this.adding=false;
        button.removeAttribute('data-adding');
        if(this.config.nativeBundle){
          const variant=this.nativeVariant(this.nativeKind),card=this.querySelector(`[data-atf-native-offer="${this.nativeKind}"]`);
          this.renderPurchaseLabel(this.config.nativeCta.replace('__choice__',this.nativeKind==='set'?this.config.nativeBundle.title:card.querySelector('.quantity').textContent.trim()),{total:variant.price,original:variant.compare_at_price||variant.price});
        }else this.reflectSelection();
        if(this.purchaseError){status.textContent=this.purchaseError;this.purchaseError=null;}
      }
    }

    message(key,values) {
      return Object.entries(values).reduce((text,[name,value]) => text.replaceAll('__'+name+'__',String(value)),this.copy[key]);
    }

    quote(tier,variants) {
      if(this.config.quantityOffer&&this.offer)return window.CloudNineQuantityOffers.quote(this.offer,variants);
      return variants.reduce((price,variant) => ({original:price.original+(variant.compare_at_price || variant.price),
        total:price.total+variant.price-Math.floor(variant.price*tier.percentage/100)}),{original:0,total:0});
    }

    nativeVariant(kind) {
      const color=this.querySelector('[data-native-color]').value,size=this.querySelector('[data-native-size]').value;
      if(kind==='set'){const options=this.config.nativeBundle.options;return this.config.nativeBundle.variants.find(variant=>variant.options[options.indexOf('Color')]===color&&variant.options[options.indexOf('Size')]===size);}
      return this.variants.find(variant=>variant.options[this.colorIndex]===color&&variant.options[this.sizeIndex]===size&&!variant.options[this.offerIndex].includes('Set'));
    }

    initializeNativeSet() {
      this.nativeKind=this.config.selectedVariant?(this.initial.options[this.offerIndex].includes('Set')?'set':'hoodie'):'set';
      const config=this.querySelector('[data-native-config]'),slot=this.querySelector('.atf-purchase-slot'),reassurance=this.querySelector('#groundingPurchaseReassurance');
      const cards=[...this.querySelectorAll('[data-atf-native-offer]')];
      const color=this.querySelector('[data-native-color]'),size=this.querySelector('[data-native-size]');
      if(!this.config.selectedVariant){
        const bundle=this.config.nativeBundle;
        color.value=bundle.defaultVariant.options[bundle.options.indexOf('Color')];
        size.value=bundle.defaultVariant.options[bundle.options.indexOf('Size')];
      }
      const dropdown=this.querySelector('.hoodie-color-dropdown'),trigger=dropdown.querySelector('.hoodie-color-trigger'),list=dropdown.querySelector('[role="listbox"]'),options=[...list.children];
      const close=(focus=false)=>{list.hidden=true;trigger.setAttribute('aria-expanded','false');if(focus)trigger.focus({preventScroll:true});};
      const reflect=()=>{
        options.forEach(option=>option.setAttribute('aria-selected',String(option.dataset.color===color.value)));
        const swatch=options.find(option=>option.dataset.color===color.value).querySelector('.hoodie-color-swatch').cloneNode(true);
        const label=document.createElement('span');label.className='hoodie-color-name';label.textContent=color.value;
        trigger.replaceChildren(swatch,label,this.querySelector('[data-native-size]').nextElementSibling.cloneNode(true));
        trigger.setAttribute('aria-label',this.copy.colorFor.replace('__number__','1')+': '+color.value);
        cards.forEach(card=>{
          const kind=card.dataset.atfNativeOffer,variant=this.nativeVariant(kind),active=kind===this.nativeKind;
          card.classList.toggle('active',active);card.querySelector('[role="radio"]').setAttribute('aria-checked',String(active));
          card.querySelector('[data-native-price]').textContent=this.money(variant.price);
          card.querySelector('[data-native-compare]').textContent=variant.compare_at_price>variant.price?this.money(variant.compare_at_price):'';
          card.querySelector('[data-native-savings]').textContent=variant.compare_at_price>variant.price?this.message('cartSavings',{amount:this.money(variant.compare_at_price-variant.price)}):'';
          if(active)card.append(config,slot,reassurance);
        });
        config.querySelector('[data-native-hoodie-label]').hidden=this.nativeKind!=='set';
        const pantsRow=config.querySelector('[data-native-pants]');
        pantsRow.hidden=this.nativeKind!=='set';
        if(this.nativeKind==='set'){
          const pants=this.config.nativePants;
          const variant=pants.variants.find(item=>item.options[pants.options.indexOf('Color')]===color.value&&item.options[pants.options.indexOf('Size')]===size.value);
          const image=document.createElement('img');
          image.className='compact-hoodie-preview';
          image.src=variant.image;image.alt=variant.alt;image.width=80;image.height=100;image.loading='lazy';
          pantsRow.querySelector('[data-native-pants-photo]').replaceChildren(image);
          pantsRow.querySelector('[data-native-pants-color]').textContent=color.value;
          pantsRow.querySelector('[data-native-pants-size]').textContent=size.value;
        }
        const variant=this.nativeVariant(this.nativeKind);
        this.renderPurchaseLabel(this.config.nativeCta.replace('__choice__',this.nativeKind==='set'?this.config.nativeBundle.title:cards[0].querySelector('.quantity').textContent.trim()),{total:variant.price,original:variant.compare_at_price||variant.price});
        const media=this.media.get(this.mediaIds(color.value)[0]).content.querySelector('img');
        const thumbnail=media.cloneNode(true);
        thumbnail.className='compact-hoodie-preview';
        thumbnail.sizes='40px';
        thumbnail.loading='lazy';
        this.querySelector('[data-native-preview]').replaceChildren(thumbnail);
        this.renderGallery(color.value);
      };
      trigger.addEventListener('click',()=>{if(!list.hidden){close();return;}list.hidden=false;trigger.setAttribute('aria-expanded','true');options.find(option=>option.dataset.color===color.value).focus({preventScroll:true});});
      trigger.addEventListener('keydown',event=>{if(['ArrowDown','ArrowUp'].includes(event.key)){event.preventDefault();trigger.click();}});
      options.forEach((option,index)=>{
        const choose=()=>{color.value=option.dataset.color;reflect();close(true);};
        option.addEventListener('click',choose);
        option.addEventListener('keydown',event=>{
          if(['Enter',' '].includes(event.key)){event.preventDefault();choose();}
          if(event.key==='Escape'){event.preventDefault();close(true);}
          if(['ArrowDown','ArrowUp','Home','End'].includes(event.key)){event.preventDefault();const next=event.key==='Home'?0:event.key==='End'?options.length-1:(index+(event.key==='ArrowDown'?1:-1)+options.length)%options.length;options[next].focus();}
        });
      });
      dropdown.addEventListener('focusout',event=>{if(!dropdown.contains(event.relatedTarget))close();});
      document.addEventListener('pointerdown',event=>{if(!dropdown.contains(event.target))close();});
      cards.forEach(card=>{
        const select=()=>{this.nativeKind=card.dataset.atfNativeOffer;close();reflect();};
        card.addEventListener('click',event=>{if(event.target.closest('select,button,input,a,.bundle-inline-config'))return;select();});
        card.querySelector('[role="radio"]').addEventListener('keydown',event=>{if(['Enter',' '].includes(event.key)){event.preventDefault();select();}});
      });
      color.addEventListener('change',reflect);size.addEventListener('change',reflect);
      this.querySelector('[data-native-preview]').addEventListener('click',event=>this.viewer.open({photos:this.photos(this.mediaIds(color.value)),trigger:event.currentTarget}));
      this.querySelector('[data-native-pants-photo]').addEventListener('click',event=>this.viewer.open({
        photos:this.config.nativePants.galleries.find(group=>group.color===color.value).photos,trigger:event.currentTarget}));
      this.initializeNativePurchaseBar(slot);
      reflect();
    }

    initializeNativePurchaseBar(slot) {
      const bar=slot.querySelector('.atf-purchase-bar'),mobile=matchMedia('(max-width:900px)');
      let seen=false,frame;
      const space=()=>{document.body.style.setProperty('--grounding-purchase-height',bar.offsetHeight+'px');if(!bar.classList.contains('is-docked'))slot.style.minHeight=bar.offsetHeight+'px';};
      const reflect=()=>{bar.classList.toggle('is-docked',mobile.matches&&seen&&slot.getBoundingClientRect().bottom<=0);space();};
      new ResizeObserver(space).observe(bar);
      new IntersectionObserver(entries=>{if(mobile.matches&&entries[0].intersectionRatio===1)seen=true;reflect();},{threshold:[0,1]}).observe(slot);
      window.addEventListener('scroll',()=>{if(frame)return;frame=requestAnimationFrame(()=>{frame=null;reflect();});},{passive:true});
      mobile.addEventListener('change',reflect);
    }

    initializeReviewVideo() {
      const dialog=this.querySelector('[data-atf-review-dialog]'),video=dialog.querySelector('video');
      let trigger,previousOverflow;
      this.querySelectorAll('[data-atf-review-video]').forEach(button=>button.addEventListener('click',()=>{
        trigger=button;previousOverflow=document.body.style.overflow;
        video.src=button.dataset.atfReviewVideo;video.poster=button.querySelector('img').src;
        document.body.style.overflow='hidden';dialog.showModal();video.play().catch(()=>{});
      }));
      dialog.querySelector('button').addEventListener('click',()=>dialog.close());
      dialog.addEventListener('click',event=>{if(event.target===dialog)dialog.close();});
      dialog.addEventListener('close',()=>{video.pause();video.removeAttribute('src');video.load();document.body.style.overflow=previousOverflow;trigger?.focus({preventScroll:true});});
    }

    mediaIds(color) {
      return window.ProductGallerySelection.mediaIdsForColor(this.groups,color,[...this.media.keys()]);
    }

    photos(ids) {
      return ids.map(id => {const image=this.media.get(id).content.querySelector('img');return {src:image.dataset.zoomSrc,alt:image.alt};});
    }

    renderGallery(color) {
      if(color===this.renderedColor)return;
      const ids=this.mediaIds(color);
      for(const [selector,className] of [['.desktop-equal-grid','equal-photo-card'],['.peek-track','peek-slide']]) {
        this.querySelector(selector).replaceChildren(...ids.map((id,index) => {
          const card=document.createElement('div');card.className=className;card.dataset.mediaId=id;
          card.tabIndex=0;card.setAttribute('role','button');card.setAttribute('aria-label','Enlarge product image');
          card.append(this.media.get(id).content.cloneNode(true));card.querySelector('img').loading=index<2?'eager':'lazy';return card;
        }));
      }
      this.renderedColor=color;
      this.querySelector('.peek-track').scrollLeft=0;
      this.reflectProgress();
    }

    reflectProgress() {
      const track=this.querySelector('.peek-track'),slides=[...track.children],progress=this.querySelector('#galleryProgress');
      if(!slides.length){progress.hidden=true;return;}
      const step=slides.length>1?slides[1].offsetLeft-slides[0].offsetLeft:1;
      const max=track.scrollWidth-track.clientWidth;
      const index=max>0&&track.scrollLeft>=max-2?slides.length-1:Math.min(slides.length-1,Math.max(0,Math.round(track.scrollLeft/step)));
      progress.hidden=false;progress.setAttribute('aria-valuemax',slides.length);progress.setAttribute('aria-valuenow',index+1);
      progress.setAttribute('aria-valuetext',`Image ${index+1} of ${slides.length}`);progress.style.setProperty('--gallery-progress',(index+1)/slides.length);
    }

    initializeFeaturePhotos() {
      const cards=[...this.querySelectorAll('.tab-feature-media[data-lightbox-src]')];
      const photos=cards.map(card=>({src:card.dataset.lightboxSrc,alt:card.querySelector('img').alt}));
      cards.forEach((card,index)=>{
        let pointer;
        card.addEventListener('pointerdown',event=>{pointer={x:event.clientX,y:event.clientY};});
        const open=event=>{
          if(event.type==='click'&&event.detail&&pointer&&Math.hypot(event.clientX-pointer.x,event.clientY-pointer.y)>12)return;
          event.preventDefault();
          event.stopPropagation();
          this.viewer.open({photos,index,trigger:card});
        };
        card.addEventListener('click',open);
        card.addEventListener('keydown',event=>{
          if(event.key==='Enter'||event.key===' ')open(event);
        });
      });
    }

    initializeTabs() {
      const tabs=[...this.querySelectorAll('.tab-btn[data-tab-index]')];
      tabs.forEach((button,index) => {
        button.addEventListener('click',() => {
          tabs.forEach(tab => {const active=tab===button;tab.classList.toggle('active',active);tab.setAttribute('aria-selected',String(active));});
          this.querySelectorAll('.tab-pane').forEach((pane,i) => pane.classList.toggle('active',i===index));
          this.querySelector('#tabContentCard').dataset.activeColor=button.dataset.color;
          this.querySelector('.tab-section').style.setProperty('--atf-panel-border',getComputedStyle(button).getPropertyValue('--benefit-border'));
        });
        button.addEventListener('keydown',event => {
          if(!['ArrowLeft','ArrowRight','Home','End'].includes(event.key))return;
          event.preventDefault();
          const next=event.key==='Home'?0:event.key==='End'?tabs.length-1:(index+(event.key==='ArrowRight'?1:-1)+tabs.length)%tabs.length;
          tabs[next].focus();tabs[next].click();
        });
      });
    }

    initializeSizeGuide() {
      const overlay=this.querySelector('[data-atf-size-overlay]'),drawer=overlay.querySelector('.cn-size-drawer'),close=overlay.querySelector('.cn-size-close');
      let previousOverflow='',trigger,unit='in';
      const region=overlay.querySelector('[data-atf-region]');
      const render=nextUnit => {
        unit=nextUnit;
        const chart=this.querySelector(`[data-atf-chart="${region?.value || 'us'}-${unit}"]`),host=this.querySelector('[data-atf-chart-live]');
        host.replaceChildren(chart.content.cloneNode(true));
        if(!host.textContent.trim())host.textContent=host.dataset.emptyMessage;
        overlay.querySelectorAll('[data-atf-unit]').forEach(button => {button.classList.toggle('active',button.dataset.atfUnit===unit);button.setAttribute('aria-pressed',String(button.dataset.atfUnit===unit));});
      };
      const dismiss=() => {overlay.classList.remove('open');overlay.setAttribute('aria-hidden','true');overlay.inert=true;document.body.style.overflow=previousOverflow;trigger?.focus({preventScroll:true});};
      overlay.inert=true;
      this.querySelector('[data-size-guide-trigger]')?.addEventListener('click',event => {
        trigger=event.currentTarget;previousOverflow=document.body.style.overflow;document.body.style.overflow='hidden';
        render('in');overlay.inert=false;overlay.classList.add('open');overlay.setAttribute('aria-hidden','false');close.focus({preventScroll:true});
      });
      close.addEventListener('click',dismiss);
      overlay.addEventListener('click',event => {if(event.target===overlay)dismiss();});
      overlay.addEventListener('keydown',event => {
        if(event.key==='Escape'){event.preventDefault();dismiss();}
        if(event.key==='Tab'){
          const focusable=[...drawer.querySelectorAll('button:not([disabled]),a[href],select,[tabindex="0"]')];
          if(event.shiftKey&&document.activeElement===focusable[0]){event.preventDefault();focusable.at(-1).focus();}
          else if(!event.shiftKey&&document.activeElement===focusable.at(-1)){event.preventDefault();focusable[0].focus();}
        }
      });
      overlay.querySelectorAll('[data-atf-unit]').forEach(button => button.addEventListener('click',()=>render(button.dataset.atfUnit)));
      region?.addEventListener('change',()=>render(unit));
    }
  }
  customElements.define('product-atf-global',ProductAtfGlobal);
})();
